<template>
  <div class="custom-slider" @mouseenter="pauseAutoplay" @mouseleave="resumeAutoplay">
    <div class="slider-container h-72 md:h-96">
      <transition-group name="fade" tag="div">
        <div v-for="(slide, index) in slides" :key="slide.id" v-show="currentSlide === index" class="slide">
          <img v-if="slide.type === 'image'" :src="slide.src" :alt="'Slide ' + (index + 1)" @click="openLightbox(index)" class="slide-image md:rounded-r-full h-full block object-cover w-full cursor-pointer" />
          <div v-else-if="slide.type === 'youtube'" class="video-wrapper md:rounded-r-full h-full" @click="openLightbox(index)">
            <img :src="slide.thumbnail" :alt="'Video thumbnail ' + (index + 1)" class="video-thumbnail md:rounded-r-full h-full block object-cover w-full" />
            <div class="video-overlay">
              <i class="fas fa-play-circle fa-3x text-pink-500 bg-white rounded-full p-1 play-button" :style="playButtonStyle"></i>
            </div>
          </div>
        </div>
      </transition-group>
    </div>

    <div class="navigation-wrapper">
      <button @click="prevSlide" class="nav-button prev">
        <span class="fa-stack">
          <i class="fal fa-circle fa-stack-2x"></i>
          <i class="fa-sharp fa-regular fa-chevron-left fa-stack-1x"></i>
        </span>
      </button>
      <button @click="nextSlide" class="nav-button next">
        <span class="fa-stack">
          <i class="fal fa-circle fa-stack-2x"></i>
          <i class="fa-sharp fa-regular fa-chevron-right fa-stack-1x"></i>
        </span>
      </button>
    </div>

    <div :class="['custom-pagination', { 'custom-pagination-small': isFirstSlider }]"
          :style="paginationStyle">
      <span
          v-for="(_, index) in slides"
          :key="index"
          :class="['pagination-bullet', { 'pagination-bullet-small': isFirstSlider, active: currentSlide === index }]"
          @click="goToSlide(index)">
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomSlider',
  props: {
    slides: {
      type: Array,
      required: true
    },
    autoplay: {
      type: Boolean,
      default: true
    },
    isFirstSlider: {
      type: Boolean,
      default: false
    },
    bulletOffset: {
      type: Number,
      default: 15
    },
    playButtonOffset: {
      type: Number,
      default: 0
    },
    isLightboxOpen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentSlide: 0,
      autoplayInterval: null,
      isAutoplayPaused: false,
      isMobile: false
    }
  },
  mounted() {
    if (this.autoplay && !this.isLightboxOpen) {
      this.startAutoplay();
    }
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
  },
  beforeUnmount() {
    this.stopAutoplay();
    window.removeEventListener('resize', this.checkMobile);
  },
  methods: {
    nextSlide() {
      const nextIndex = (this.currentSlide + 1) % this.slides.length;
      this.goToSlide(nextIndex);
    },
    prevSlide() {
      const prevIndex = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
      this.goToSlide(prevIndex);
    },
    goToSlide(index) {
      this.currentSlide = index;
      this.$emit('slide-changed', index);
      this.resetAutoplay();
    },
    startAutoplay() {
      if (!this.isAutoplayPaused && !this.isLightboxOpen) {
        this.stopAutoplay(); // Clear any existing interval
        this.autoplayInterval = setInterval(() => {
          this.nextSlide();
        }, 5000);
      }
    },
    stopAutoplay() {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    },
    pauseAutoplay() {
      this.isAutoplayPaused = true;
      this.stopAutoplay();
    },
    resumeAutoplay() {
      this.isAutoplayPaused = false;
      if (this.autoplay && !this.isLightboxOpen) {
        this.startAutoplay();
      }
    },
    resetAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    },
    openLightbox(index) {
      this.$emit('open-lightbox', index);
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
  },
  watch: {
    isLightboxOpen: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.pauseAutoplay();
        } else {
          this.$nextTick(() => {
            this.resumeAutoplay();
          });
        }
      }
    }
  },
  computed: {
    paginationStyle() {
      if (!this.isFirstSlider) return {};

      const bulletWidth = 8; // Width of each bullet
      const bulletMargin = 4; // Margin between bullets
      const totalWidth = this.slides.length * (bulletWidth + 2 * bulletMargin);
      const curveOffset = this.isMobile ? 0 : 40; // Apply curve offset only for non-mobile devices

      return {
        left: `calc(50% - ${totalWidth / 2}px - ${curveOffset}px)`,
        transform: 'translateX(0)',
        bottom: '15px',
      };
    },
    playButtonStyle() {
      if (!this.isFirstSlider) return { transform: 'translate(-50%, -50%)' };
      
      const curveOffset = this.isMobile ? 0 : 40; // Apply curve offset only for non-mobile devices
      
      return {
        transform: `translate(calc(-50% - ${curveOffset}px), -50%)`,
        position: 'absolute',
        top: '50%',
        left: '50%',
      };
    }
  },
}
</script>

<style scoped>
.custom-slider {
  position: relative;
  width: 100%;
}

.slider-container {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.slide-image, .video-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.video-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.play-button {
  font-size: 4rem;
  transition: transform 0.2s ease-in-out;
}

.video-wrapper:hover .play-button {
  transform: scale(1.1);
}

.navigation-wrapper {
  position: absolute;
  left: 10px;
  bottom: 20px;
  display: flex;
  z-index: 10;
}

.nav-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0 5px;
}

.nav-button .fa-stack-2x {
  color: #fff;
}

.nav-button .fa-stack-1x {
  color: #fff;
}

.custom-pagination {
  position: absolute;
  bottom: 15px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
}

.pagination-bullet {
  width: 20px;
  height: 20px;
  background-color: #DA1884;
  opacity: 0.4;
  margin: 0 6px;
  border-radius: 50%;
  transition: opacity 0.3s;
  cursor: pointer;
}

.pagination-bullet.active {
  opacity: 1;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.custom-pagination-small {
  position: absolute;
  bottom: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.pagination-bullet-small {
  width: 8px;
  height: 8px;
  background-color: rgba(255, 255, 255, 0.5);
  opacity: 0.6;
  margin: 0 4px;
  border-radius: 50%;
  transition: opacity 0.3s, background-color 0.3s;
  cursor: pointer;
}

.pagination-bullet-small.active {
  opacity: 1;
  background-color: #fff;
}

@media (max-width: 768px) {
  .custom-pagination-small {
    bottom: 10px;
  }
  
  .video-overlay i {
    transform: translate(-50%, -50%) !important;
  }
}
</style>
